import type { TrainingApplyStatus } from "@bbj-types/apply/training";
import type { ITrainingOffer } from "@bbj-types/offer";
import type { ObjectId } from "@bbj-types/utils";
import { useCallback } from "react";
import type { TTrainingOfferCreation } from "../../offers/trainingOffer/createTrainingOffer";
import { axios } from "../axios";

export type TManualTrainingApplyOffer = TTrainingOfferCreation &
  Pick<ITrainingOffer, "active" | "isExpired" | "offerId" | "publicationDate" | "publicationUpdatedDate" | "source">;

export type TTrainingApplyOfferCreation = TTrainingOfferCreation & { documents: string[] };
export type TTrainingApplyOffer = ITrainingOffer | TManualTrainingApplyOffer;

type TTrainingOfferDurationUnit = NonNullable<NonNullable<ITrainingOffer["trainingDuration"]>["unit"]>;

export interface IPostTrainingAppliesCandidateExistingOfferBody {
  status: TrainingApplyStatus;
  offer: TTrainingApplyOffer;
}

export interface IPostTrainingAppliesCandidateCreateOfferBody {
  status: TrainingApplyStatus;
  createAssociatedOffer: true;
  offer: TTrainingApplyOfferCreation;
}

export type IPostTrainingAppliesCandidateBody =
  | IPostTrainingAppliesCandidateExistingOfferBody
  | IPostTrainingAppliesCandidateCreateOfferBody;

export interface IPostTrainingAppliesCandidateResponseItem {
  _id: ObjectId;
  offer: {
    name: string;
    provider?: {
      name: string;
    };
    address?: {
      city: string;
    };
    trainingDuration?: {
      duration: number;
      unit: TTrainingOfferDurationUnit;
    };
    trainingStartDate?: Date;
    trainingEndDate?: Date;
  };
}

export type IPostTrainingAppliesCandidateResponse = IPostTrainingAppliesCandidateResponseItem[];

export const usePostTrainingAppliesCandidate = () => {
  const [state, execute, cancel] = axios<
    IPostTrainingAppliesCandidateResponse,
    IPostTrainingAppliesCandidateBody[],
    unknown
  >(
    {
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (candidateId: ObjectId, body: IPostTrainingAppliesCandidateBody[]) => {
      return execute({
        url: `/applies/training/candidate/${candidateId}`,
        data: body,
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};
